import { useState, useMemo, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import AppLayout from '@/components/feature/AppLayout';
import { useToast } from '@/contexts/ToastContext';
import { financeApi, type CoaAccount } from '@/api/financeApi';
import { formatINR } from '@/utils/format';

interface EntryRow {
  accountId: string;
  accountCode: string;
  accountName: string;
  debit: number;
  credit: number;
  narration: string;
}

function emptyRow(initialAccountId = ''): EntryRow {
  return { accountId: initialAccountId, accountCode: '', accountName: '', debit: 0, credit: 0, narration: '' };
}

export default function NewJournalVoucherPage() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const toast = useToast();

  const [date, setDate] = useState(new Date().toISOString().split('T')[0]);
  const [narration, setNarration] = useState('');
  const [rows, setRows] = useState<EntryRow[]>([emptyRow(), emptyRow()]);
  const [accounts, setAccounts] = useState<CoaAccount[]>([]);
  const [saving, setSaving] = useState(false);

  const preselectedAccountId = searchParams.get('accountId');

  useEffect(() => {
    async function loadAccounts() {
      try {
        // Control accounts (Debtors, Creditors, stock, GRN clearing…) are fed by their
        // modules and cannot be used in a manual journal
        const accs = (await financeApi.getAccounts({ isActive: true })).filter((a) => a.allowDirectPosting);
        setAccounts(accs);
        if (preselectedAccountId && accs.length > 0) {
          const match = accs.find((a) => a.id === preselectedAccountId);
          if (match) {
            setRows([
              { accountId: match.id, accountCode: match.code, accountName: match.name, debit: 0, credit: 0, narration: '' },
              emptyRow(),
            ]);
          }
        }
      } catch (err) {
        console.error('Failed to load accounts for JV:', err);
      }
    }
    loadAccounts();
  }, [preselectedAccountId]);

  const totals = useMemo(() => {
    const debit = rows.reduce((s, r) => s + (Number(r.debit) || 0), 0);
    const credit = rows.reduce((s, r) => s + (Number(r.credit) || 0), 0);
    return {
      debit: Number(debit.toFixed(2)),
      credit: Number(credit.toFixed(2)),
    };
  }, [rows]);

  const diff = Number(Math.abs(totals.debit - totals.credit).toFixed(2));
  const isBalanced = diff < 0.01 && totals.debit > 0;

  const updateRow = (idx: number, patch: Partial<EntryRow>) => {
    setRows((prev) => prev.map((r, i) => (i === idx ? { ...r, ...patch } : r)));
  };

  const handleAccountSelect = (idx: number, accountId: string) => {
    const acc = accounts.find((a) => a.id === accountId);
    if (acc) {
      updateRow(idx, { accountId: acc.id, accountCode: acc.code, accountName: acc.name });
    }
  };

  const addRow = () => setRows((prev) => [...prev, emptyRow()]);

  const removeRow = (idx: number) => {
    if (rows.length <= 2) {
      toast.error('A journal voucher requires at least two rows.');
      return;
    }
    setRows((prev) => prev.filter((_, i) => i !== idx));
  };

  const applyTemplate = (templateName: string) => {
    const salaryAcc = accounts.find((a) => a.name.toLowerCase().includes('salar') || a.systemTag === 'SALARIES');
    const bankAcc = accounts.find((a) => a.systemTag === 'BANK');
    const cashAcc = accounts.find((a) => a.systemTag === 'CASH');
    const rentAcc = accounts.find((a) => a.name.toLowerCase().includes('rent'));
    const depAcc = accounts.find((a) => a.name.toLowerCase().includes('depreciation') && a.type === 'EXPENSE');
    const accDepAcc = accounts.find((a) => a.systemTag === 'ACCUMULATED_DEPRECIATION');

    if (templateName === 'salary' && salaryAcc && bankAcc) {
      setRows([
        { accountId: salaryAcc.id, accountCode: salaryAcc.code, accountName: salaryAcc.name, debit: 0, credit: 0, narration: 'Monthly Salaries & Wages' },
        { accountId: bankAcc.id, accountCode: bankAcc.code, accountName: bankAcc.name, debit: 0, credit: 0, narration: 'Disbursed from Bank' },
      ]);
      setNarration('Monthly salary disbursement');
    } else if (templateName === 'rent' && rentAcc && bankAcc) {
      setRows([
        { accountId: rentAcc.id, accountCode: rentAcc.code, accountName: rentAcc.name, debit: 0, credit: 0, narration: 'Office Rent Payment' },
        { accountId: bankAcc.id, accountCode: bankAcc.code, accountName: bankAcc.name, debit: 0, credit: 0, narration: 'Paid via Bank Transfer' },
      ]);
      setNarration('Office rent payment');
    } else if (templateName === 'depreciation' && depAcc && accDepAcc) {
      setRows([
        { accountId: depAcc.id, accountCode: depAcc.code, accountName: depAcc.name, debit: 0, credit: 0, narration: 'Monthly Depreciation Charge' },
        { accountId: accDepAcc.id, accountCode: accDepAcc.code, accountName: accDepAcc.name, debit: 0, credit: 0, narration: 'Accumulated Depreciation' },
      ]);
      setNarration('Monthly depreciation provision');
    } else if (templateName === 'cash-bank' && cashAcc && bankAcc) {
      setRows([
        { accountId: cashAcc.id, accountCode: cashAcc.code, accountName: cashAcc.name, debit: 0, credit: 0, narration: 'Cash withdrawn from Bank' },
        { accountId: bankAcc.id, accountCode: bankAcc.code, accountName: bankAcc.name, debit: 0, credit: 0, narration: 'Bank Withdrawal (Contra)' },
      ]);
      setNarration('Cash withdrawal for office use (Contra)');
    }
  };

  const handleSubmit = async (targetStatus: 'DRAFT' | 'POSTED') => {
    // Basic validations
    if (rows.length < 2) {
      toast.error('A journal voucher must have at least two entries.');
      return;
    }
    for (let i = 0; i < rows.length; i++) {
      const r = rows[i];
      if (!r.accountId) {
        toast.error(`Line ${i + 1}: Please select an account.`);
        return;
      }
      const deb = Number(r.debit) || 0;
      const cred = Number(r.credit) || 0;
      if (deb === 0 && cred === 0) {
        toast.error(`Line ${i + 1}: Enter either a debit or credit amount.`);
        return;
      }
      if (deb > 0 && cred > 0) {
        toast.error(`Line ${i + 1}: Cannot have both debit and credit on the same line.`);
        return;
      }
    }

    if (targetStatus === 'POSTED' && !isBalanced) {
      toast.error(`Voucher is unbalanced! Debits (${formatINR(totals.debit)}) must equal Credits (${formatINR(totals.credit)}).`);
      return;
    }

    try {
      setSaving(true);
      const voucher = await financeApi.createJournalVoucher({
        voucherDate: date,
        voucherType: 'MANUAL',
        narration: narration.trim() || 'Manual Journal Voucher',
        status: targetStatus,
        entries: rows.map((r) => ({
          accountId: r.accountId,
          debit: Number(r.debit) || 0,
          credit: Number(r.credit) || 0,
          narration: r.narration || narration || undefined,
        })),
      });

      toast.success(
        targetStatus === 'POSTED'
          ? `Journal Voucher ${voucher.voucherNumber} posted to General Ledger.`
          : `Journal Voucher ${voucher.voucherNumber} saved as draft.`
      );
      navigate('/finance/journal');
    } catch (err: any) {
      toast.error(err.response?.data?.message || 'Failed to save journal voucher');
    } finally {
      setSaving(false);
    }
  };

  return (
    <AppLayout>
      <div className="flex flex-col h-full bg-[#f8fafc]">
        <div className="flex-1 overflow-y-auto pb-16">
          {/* Header */}
          <div className="flex items-center justify-between px-6 pt-6">
            <div className="flex items-center gap-3">
              <button
                onClick={() => navigate('/finance/journal')}
                className="w-8 h-8 rounded-lg border border-[#e2e8f0] flex items-center justify-center text-slate-600 hover:bg-slate-100 transition-colors"
              >
                <i className="ri-arrow-left-line text-sm" />
              </button>
              <div>
                <h1 className="text-xl font-bold text-[#1e293b]">New Journal Voucher</h1>
                <p className="text-xs text-slate-500">Record a double-entry journal transaction</p>
              </div>
            </div>

            <div className="flex items-center gap-2">
              <button
                disabled={saving}
                onClick={() => handleSubmit('DRAFT')}
                className="h-9 px-4 rounded-lg border border-[#e2e8f0] bg-white text-sm font-medium text-slate-700 hover:bg-slate-50 transition-colors cursor-pointer"
              >
                Save as Draft
              </button>
              <button
                disabled={saving}
                onClick={() => handleSubmit('POSTED')}
                className={`px-5 py-2 rounded-lg text-xs font-semibold text-white transition-colors shadow-sm cursor-pointer ${
                  isBalanced
                    ? 'bg-[#4f46e5] hover:bg-indigo-700'
                    : 'bg-slate-400 cursor-not-allowed opacity-80'
                }`}
              >
                {saving ? 'Posting...' : 'Post to Ledger'}
              </button>
            </div>
          </div>

          <div className="px-6 py-5 max-w-6xl mx-auto space-y-4">
            {/* Quick Templates */}
            <div className="bg-white border border-[#e2e8f0] rounded-xl p-3.5 shadow-sm flex items-center justify-between gap-3 flex-wrap">
              <span className="text-xs font-bold text-slate-700 flex items-center gap-1.5">
                <i className="ri-magic-line text-primary-500 text-sm" /> Quick Templates:
              </span>
              <div className="flex items-center gap-2 flex-wrap">
                <button
                  type="button"
                  onClick={() => applyTemplate('salary')}
                  className="px-2.5 py-1 text-[11px] font-semibold rounded-lg bg-indigo-50 text-indigo-700 border border-indigo-200 hover:bg-indigo-100 transition-colors"
                >
                  Salary Payment
                </button>
                <button
                  type="button"
                  onClick={() => applyTemplate('rent')}
                  className="px-2.5 py-1 text-[11px] font-semibold rounded-lg bg-blue-50 text-blue-700 border border-blue-200 hover:bg-blue-100 transition-colors"
                >
                  Rent Payment
                </button>
                <button
                  type="button"
                  onClick={() => applyTemplate('depreciation')}
                  className="px-2.5 py-1 text-[11px] font-semibold rounded-lg bg-amber-50 text-amber-800 border border-amber-200 hover:bg-amber-100 transition-colors"
                >
                  Depreciation Provision
                </button>
                <button
                  type="button"
                  onClick={() => applyTemplate('cash-bank')}
                  className="px-2.5 py-1 text-[11px] font-semibold rounded-lg bg-emerald-50 text-emerald-800 border border-emerald-200 hover:bg-emerald-100 transition-colors"
                >
                  Cash / Bank Contra
                </button>
              </div>
            </div>

            {/* Voucher Header Info */}
            <div className="bg-white border border-[#e2e8f0] rounded-xl p-5 shadow-sm grid grid-cols-1 md:grid-cols-3 gap-4">
              <div>
                <label className="block text-xs font-semibold text-slate-500 mb-1">Voucher Date</label>
                <input
                  type="date"
                  value={date}
                  onChange={(e) => setDate(e.target.value)}
                  className="w-full h-9 px-3 text-sm bg-white border border-[#e2e8f0] rounded-lg focus:outline-none focus:border-[#4f46e5] text-[#1e293b]"
                />
              </div>
              <div className="md:col-span-2">
                <label className="block text-xs font-semibold text-slate-500 mb-1">General Narration</label>
                <input
                  type="text"
                  value={narration}
                  onChange={(e) => setNarration(e.target.value)}
                  placeholder="Describe the nature of this financial entry (e.g., Monthly office rent for September)"
                  className="w-full h-9 px-3 text-sm bg-white border border-[#e2e8f0] rounded-lg focus:outline-none focus:border-[#4f46e5] text-[#1e293b]"
                />
              </div>
            </div>

            {/* Journal Lines Table */}
            <div className="bg-white border border-[#e2e8f0] rounded-xl overflow-hidden shadow-sm">
              <div className="px-5 py-3 border-b border-[#e2e8f0] bg-slate-100/50 flex items-center justify-between">
                <h3 className="text-xs font-bold text-slate-800 uppercase tracking-wider">
                  Journal Entry Lines ({rows.length})
                </h3>
                <button
                  type="button"
                  onClick={addRow}
                  className="text-xs font-semibold text-primary-600 hover:text-primary-800 flex items-center gap-1 cursor-pointer"
                >
                  <i className="ri-add-line text-sm" /> Add Line
                </button>
              </div>

              <table className="w-full text-xs">
                <thead className="bg-slate-50 border-b border-slate-100 text-[10px] text-slate-400 uppercase tracking-wider font-semibold">
                  <tr>
                    <th className="text-left px-4 py-2.5 w-12 text-center">#</th>
                    <th className="text-left px-4 py-2.5 w-72">Account</th>
                    <th className="text-right px-4 py-2.5 w-36">Debit (Dr ₹)</th>
                    <th className="text-right px-4 py-2.5 w-36">Credit (Cr ₹)</th>
                    <th className="text-left px-4 py-2.5">Line Narration</th>
                    <th className="w-12 text-center px-2 py-2.5"></th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-slate-100">
                  {rows.map((row, idx) => (
                    <tr key={idx} className="hover:bg-slate-50/50">
                      <td className="px-4 py-2 text-center text-slate-400 font-mono text-[11px]">{idx + 1}</td>
                      <td className="px-4 py-2">
                        <select
                          value={row.accountId}
                          onChange={(e) => handleAccountSelect(idx, e.target.value)}
                          className="w-full h-8 px-2 text-sm border border-[#e2e8f0] rounded-lg focus:outline-none focus:border-[#4f46e5] text-[#1e293b] bg-white"
                        >
                          <option value="">-- Select Account --</option>
                          {accounts.map((acc) => (
                            <option key={acc.id} value={acc.id}>
                              {acc.code} — {acc.name} ({acc.type})
                            </option>
                          ))}
                        </select>
                      </td>
                      <td className="px-4 py-2">
                        <input
                          type="number"
                          step="0.01"
                          min="0"
                          value={row.debit || ''}
                          onChange={(e) => {
                            const val = parseFloat(e.target.value) || 0;
                            updateRow(idx, { debit: val, credit: val > 0 ? 0 : row.credit });
                          }}
                          placeholder="0.00"
                          className="w-full h-8 px-2 text-sm text-right font-mono border border-[#e2e8f0] rounded-lg focus:outline-none focus:border-[#4f46e5] text-[#1e293b] font-semibold"
                        />
                      </td>
                      <td className="px-4 py-2">
                        <input
                          type="number"
                          step="0.01"
                          min="0"
                          value={row.credit || ''}
                          onChange={(e) => {
                            const val = parseFloat(e.target.value) || 0;
                            updateRow(idx, { credit: val, debit: val > 0 ? 0 : row.debit });
                          }}
                          placeholder="0.00"
                          className="w-full h-8 px-2 text-sm text-right font-mono border border-[#e2e8f0] rounded-lg focus:outline-none focus:border-[#4f46e5] text-[#1e293b] font-semibold"
                        />
                      </td>
                      <td className="px-4 py-2">
                        <input
                          type="text"
                          value={row.narration}
                          onChange={(e) => updateRow(idx, { narration: e.target.value })}
                          placeholder="Optional line remarks"
                          className="w-full h-8 px-2 text-sm border border-[#e2e8f0] rounded-lg focus:outline-none focus:border-[#4f46e5] text-[#1e293b]"
                        />
                      </td>
                      <td className="px-2 py-2 text-center">
                        <button
                          type="button"
                          onClick={() => removeRow(idx)}
                          className="w-7 h-7 rounded text-slate-400 hover:text-red-600 hover:bg-red-50 flex items-center justify-center transition-colors"
                          title="Remove line"
                        >
                          <i className="ri-delete-bin-line text-sm" />
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
                <tfoot className="bg-slate-100/60 font-bold border-t-2 border-[#e2e8f0]">
                  <tr>
                    <td colSpan={2} className="px-4 py-3 text-right uppercase tracking-wider text-xs text-slate-700">
                      Total
                    </td>
                    <td className="px-4 py-3 text-right font-mono text-sm text-[#1e293b]">
                      {formatINR(totals.debit)}
                    </td>
                    <td className="px-4 py-3 text-right font-mono text-sm text-[#1e293b]">
                      {formatINR(totals.credit)}
                    </td>
                    <td colSpan={2} className="px-4 py-3 text-right">
                      {isBalanced ? (
                        <span className="inline-flex items-center gap-1 text-xs text-emerald-700 font-bold bg-emerald-50 px-2.5 py-1 rounded-full border border-emerald-200">
                          <i className="ri-checkbox-circle-fill text-emerald-600" /> Balanced
                        </span>
                      ) : (
                        <span className="inline-flex items-center gap-1 text-xs text-rose-700 font-bold bg-rose-50 px-2.5 py-1 rounded-full border border-rose-200">
                          <i className="ri-alert-fill text-rose-600" /> Difference: {formatINR(diff)}
                        </span>
                      )}
                    </td>
                  </tr>
                </tfoot>
              </table>
            </div>

            {/* Bottom action buttons */}
            <div className="flex items-center justify-between pt-2">
              <button
                type="button"
                onClick={addRow}
                className="h-9 px-4 rounded-lg border border-dashed border-slate-300 text-sm font-medium text-primary-600 hover:bg-primary-50 transition-colors flex items-center gap-1.5"
              >
                <i className="ri-add-line text-sm" /> Add Another Row
              </button>

              <div className="flex items-center gap-3">
                <button
                  type="button"
                  onClick={() => navigate('/finance/journal')}
                  className="h-9 px-4 text-sm font-medium text-slate-700 border border-[#e2e8f0] bg-white hover:bg-slate-50 rounded-lg transition-colors"
                >
                  Cancel
                </button>
                <button
                  disabled={saving}
                  onClick={() => handleSubmit('DRAFT')}
                  className="h-9 px-4 rounded-lg border border-[#e2e8f0] bg-white text-sm font-medium text-slate-700 hover:bg-slate-50 transition-colors"
                >
                  Save as Draft
                </button>
                <button
                  disabled={saving || !isBalanced}
                  onClick={() => handleSubmit('POSTED')}
                  className={`px-5 py-2 rounded-lg text-xs font-semibold text-white transition-colors shadow-sm ${
                    isBalanced
                      ? 'bg-[#4f46e5] hover:bg-indigo-700 cursor-pointer'
                      : 'bg-slate-400 cursor-not-allowed opacity-80'
                  }`}
                >
                  {saving ? 'Posting...' : 'Post to Ledger'}
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>
    </AppLayout>
  );
}